import { Badge, Button, Group, Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { bindSlot, unbindSlot, useAnalysisAction, useCandidates, type AnalysisInput, type AnalysisStaff } from '@/api/analyses';
import { errorMessage } from '@/api/errors';
import { useFormat } from '@/components/useFormat';
import { objectName } from '@/features/objects/objectName';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { brand } from '@/theme/theme';

export interface SlotBinderProps {
  analysis: Pick<AnalysisStaff, 'public_id' | 'state'>;
  projectId: string;
  input: AnalysisInput;
}

export const canEditBindings = (state: string | undefined) => state === 'planned' || state === 'ready';

/** One slot: what it wants, what staff bound (with digest and time), and the candidate picker. */
export function SlotBinder({ analysis, projectId, input }: SlotBinderProps) {
  const { t } = useTranslation('analyses');
  const { dateTime } = useFormat();
  const kindLabel = useKindLabel();
  const editable = canEditBindings(analysis.state);
  const cands = useCandidates(editable ? projectId : undefined, input.kind);
  const bind = useAnalysisAction(bindSlot);
  const unbind = useAnalysisAction(unbindSlot);
  const run = async (p: Promise<unknown>) => {
    try {
      await p;
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  return (
    <Group justify="space-between" align="flex-start" py="xs" style={{ borderBottom: `1px solid ${brand.border}` }} wrap="nowrap">
      <Stack gap={2} miw={220}>
        <Group gap={6}>
          <Text fw={600} fz={14}>
            {input.role}
          </Text>
          <Badge size="xs" variant="light" color={input.required ? 'forest' : 'gray'}>
            {input.required ? t('slots.required') : t('slots.optional')}
          </Badge>
        </Group>
        <Text fz={12} c={brand.textMuted}>
          {input.kind === '*' ? t('slots.anyKind') : kindLabel(input.kind)}
        </Text>
      </Stack>
      <Stack gap={4} style={{ flex: 1 }}>
        {input.data_object ? (
          <>
            <Text fz={14}>
              <Link to="/staff/objects/$objectId" params={{ objectId: input.data_object.public_id }} style={{ color: brand.text, fontWeight: 600 }}>
                {objectName(input.data_object) === input.data_object.public_id
                  ? input.data_object.notes || input.data_object.public_id
                  : objectName(input.data_object)}
              </Link>{' '}
              <Text component="span" ff="monospace" fz={11} c={brand.textMuted}>
                {input.data_object.public_id}
              </Text>
            </Text>
            <Text fz={11} ff="monospace" c={brand.textMuted}>
              {input.bound_digest || '—'} · {dateTime(input.bound_at)}
            </Text>
            {input.data_object.deleted && (
              <Text fz={12} c="red">
                {t('slots.deleted')}
              </Text>
            )}
          </>
        ) : (
          <Text fz={14} c={brand.textMuted}>
            {t('slots.unbound')}
          </Text>
        )}
      </Stack>
      {editable && (
        <Group gap="xs" wrap="nowrap">
          <Select
            placeholder={t('slots.pick')}
            data={(cands.data ?? []).map((c) => ({ value: c.public_id, label: `${objectName(c)} · ${kindLabel(c.kind)}` }))}
            value={input.data_object?.public_id ?? null}
            onChange={(pick) => {
              if (pick && pick !== input.data_object?.public_id) void run(bind.mutateAsync({ id: analysis.public_id, args: [input.role, pick] }));
            }}
            allowDeselect={false}
            disabled={bind.isPending}
            w={280}
            searchable
            nothingFoundMessage={t('slots.noCandidates')}
          />
          {input.data_object && (
            <Button size="xs" variant="default" loading={unbind.isPending} onClick={() => void run(unbind.mutateAsync({ id: analysis.public_id, args: [input.role] }))}>
              {t('slots.unbind')}
            </Button>
          )}
        </Group>
      )}
    </Group>
  );
}
